<template>
    <page-layout :hide-footer="isProposalAccepted">
        <template>
            <template slot="header">
                <proposal-step-header
                    :current-step="stepNumber"
                    :title="pageTitle"
                    :total-steps="totalSteps"
                    :is-loading="isLoading"
                    :use-exit-modal="!isProposalAccepted && agreed === true"
                />
            </template>
            <template slot="default">
                <g-loader v-if="isLoading" type="fixed" />
                <template v-else>
                    <div class="margin-bottom-large">
                        <g-typography tag="p" :value="stepOverview" class="break-new-line" />
                    </div>
                    <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
                    <proposal-step-error
                        v-if="proposalStep && proposalStep.error"
                        class="margin-bottom-normal"
                        :step="proposalStep"
                    />

                    <template v-if="hasCustomContentRights">
                        <div class="margin-bottom-large">
                            <g-typography v-html="$sanitize(customContentRights)" />
                        </div>
                    </template>
                    <template v-else>
                        <div class="margin-bottom-normal">
                            <g-typography tag="p" :value="agreementText" />
                        </div>
                        <ul class="content-rights-list margin-bottom-large">
                            <li v-for="item in standardContentRights" class="margin-bottom-small">{{ item.text }}</li>
                        </ul>
                    </template>
                    <g-form>
                        <g-form-item :required="true">
                            <g-media-object>
                                <template slot="left">
                                    <g-checkbox
                                        v-if="isProposalAccepted"
                                        :checked="proposal && proposal.agreed"
                                        class="agreement-checkbox"
                                    />
                                    <g-checkbox v-else v-model="agreed" class="agreement-checkbox" />
                                </template>
                                <template>
                                    <float-on-mask :float-up="isProposalAccepted">
                                        <g-typography v-if="!usesAlternateTerms">
                                            I accept the terms of the foregoing proposal and agree to the
                                            <a href="https://grin.co/privacy/" target="_blank">Privacy Policy</a>
                                            and
                                            <a href="https://grin.co/terms-of-use-influencers/" target="_blank"
                                                >Terms & Conditions</a
                                            >
                                        </g-typography>
                                        <g-typography v-else>
                                            I accept the above terms and conditions with {{ brandName }} and agree to
                                            the terms provided by {{ brandName }}'s service provider, Grin via the
                                            <a href="https://grin.co/privacy/" target="_blank">Grin Privacy Policy</a>
                                            and
                                            <a href="https://grin.co/terms-of-use-influencers/" target="_blank"
                                                >Grin Terms & Conditions</a
                                            >.
                                        </g-typography>
                                    </float-on-mask>
                                </template>
                            </g-media-object>
                        </g-form-item>
                    </g-form>
                </template>
            </template>
            <template slot="footer">
                <proposal-step-footer
                    :is-next-disabled="isDisabled"
                    :loading="isCompletingProposal"
                    :step-completed="isCompleted"
                    :current-step="stepNumber"
                    :total-steps="totalSteps"
                    :use-exit-modal="!isProposalAccepted && agreed === true"
                    @go-next-step="goNextStep"
                    @go-previous-step="$emit('go-previous-step', proposalStep)"
                />
            </template>
        </template>
    </page-layout>
</template>

<script>
    import ErrorMessageCard from './partials/ErrorMessageCard';
    import PageLayout from '../../layouts/PageLayout';
    import ProposalStepHeader from './partials/ProposalStepHeader';
    import ProposalStepFooter from './partials/ProposalStepFooter';
    import { getProposalStepOverview, getCustomizableTitle } from '../../../helpers/default-texts';
    import * as _ from 'lodash';
    import ProposalStepError from './partials/PoposalStepError';
    import ProposalMixin from './mixins/ProposalMixin';
    import FloatOnMask from '../../library/FloatOnMask';

    export default {
        name: 'ProposalAgreementsView',
        components: {
            FloatOnMask,
            ProposalStepError,
            ErrorMessageCard,
            PageLayout,
            ProposalStepHeader,
            ProposalStepFooter
        },
        mixins: [ProposalMixin],
        props: {
            campaign: {
                type: Object
            },
            proposal: {
                type: Object
            },
            proposalStep: {
                type: Object
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isCompletingProposal: {
                type: Boolean,
                default: false
            },
            errors: {
                type: undefined
            },
            errorMessage: {
                type: String
            },
            usesAlternateTerms: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                agreed: false
            };
        },
        computed: {
            pageTitle() {
                return getCustomizableTitle(this.campaign, 'content_rights_step_title');
            },
            stepOverview() {
                return getProposalStepOverview(
                    this.campaign,
                    'content_rights_step_overview',
                    'You must agree to these content rights to participate.'
                );
            },
            isDisabled() {
                return !this.agreed;
            },
            stepNumber() {
                return _.get(this, 'proposalStep.meta.stepNumber', 0);
            },
            totalSteps() {
                return _.get(this, 'proposalStep.meta.totalSteps', 0);
            },
            agreementText() {
                if (this.isProposalAccepted) {
                    return _.get(
                        this,
                        'proposal.content_rights.content_rights.standard_content_rights.content_rights_header',
                        ''
                    );
                }

                return _.get(
                    this,
                    'campaign.content_rights.content_rights.standard_content_rights.content_rights_header',
                    ''
                );
            },
            standardContentRights() {
                if (this.isProposalAccepted) {
                    return _.get(
                        this,
                        'proposal.content_rights.content_rights.standard_content_rights.selected_content_rights',
                        []
                    );
                }

                return _.get(
                    this,
                    'campaign.content_rights.content_rights.standard_content_rights.selected_content_rights',
                    []
                );
            },
            customContentRights() {
                if (this.isProposalAccepted) {
                    return _.get(this, 'proposal.content_rights.content_rights.custom_content_rights', null);
                }

                return _.get(this, 'campaign.content_rights.content_rights.custom_content_rights', null);
            },
            hasCustomContentRights() {
                return !!this.customContentRights;
            },
            isCompleted() {
                return _.get(this, 'proposalStep.is_complete', false);
            },
            brandName() {
                return this.campaign.brand.name;
            }
        },
        methods: {
            goNextStep() {
                this.$emit('go-next-step', this.proposalStep, { agreed: this.agreed });
            }
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .content-rights-list {
        margin-left: $g-spacing-6;
        & li {
            padding-left: $g-spacing-4;
        }
    }

    .agreement-checkbox {
        margin-right: $g-spacing-4;
    }

    .break-new-line {
        white-space: pre-line;
    }
</style>
